/**
 * services.ts
 * ----------------
 * Utility functions for services.
 */

/**
 * Get service friendly name for service identifier.
 * @param service Service identifier
 * @returns Service friendly name
 */
export function getDisplayNameForService (service: string) {
    switch (service) {
        case 'GitHubCodespaces': return 'GitHub Codespaces';
        case 'IdentitySignIn': return 'Identity/Sign-In';
        case 'Intellicode': return 'IntelliCode';
        case 'Licensing': return 'Licensing';
        case 'LiveShare': return 'Live Share';
        case 'Marketplace': return 'Marketplace';
        case 'NuGet': return 'NuGet';
        case 'Roaming': return 'Roaming';
        case 'VSFeedback': return 'Feedback';
        case 'VisualStudioSetup': return 'Visual Studio Installer';
        case 'VisualStudioCopilot': return 'GitHub Copilot';
        default: throw new Error('Could not get title for unknown service.');
    }
}

/**
 * Get icon name for service identifier.
 * @param service Service identifier
 * @returns Icon name
 */
export function getIconNameForService (service: string) {
    switch (service) {
        case 'GitHubCodespaces': return 'GitHubCodespaces';
        case 'IdentitySignIn': return 'IdentitySignIn';
        case 'Intellicode': return 'Intellicode';
        case 'Licensing': return 'Licensing';
        case 'LiveShare': return 'LiveShare';
        case 'Marketplace': return 'Marketplace';
        case 'NuGet': return 'NuGet';
        case 'Roaming': return 'Roaming';
        case 'VSFeedback': return 'Feedback';
        case 'VisualStudioSetup': return 'Setup';
        case 'VisualStudioCopilot': return 'VisualStudioCopilot';
        default: throw new Error('Could not get icon title for unknown service.');
    }
}

/**
 * Gets standardized string representation of a service identifier. Primarily used to coerce case-insensitive
 * equivalents into the proper casing.
 * @param value Value to check
 * @returns Normalized service identifier if it is a valid value, undefined otherwise
 */
export function getNormalizedServiceIdentifier (value: string) {
    if (value.toLowerCase() === 'GitHubCodespaces'.toLowerCase()) {
        return 'GitHubCodespaces';
    }

    if (value.toLowerCase() === 'IdentitySignIn'.toLowerCase()) {
        return 'IdentitySignIn';
    }

    if (value.toLowerCase() === 'Intellicode'.toLowerCase()) {
        return 'Intellicode';
    }

    if (value.toLowerCase() === 'Licensing'.toLowerCase()) {
        return 'Licensing';
    }

    if (value.toLowerCase() === 'LiveShare'.toLowerCase()) {
        return 'LiveShare';
    }

    if (value.toLowerCase() === 'Marketplace'.toLowerCase()) {
        return 'Marketplace';
    }

    if (value.toLowerCase() === 'Roaming'.toLowerCase()) {
        return 'Roaming';
    }

    if (value.toLowerCase() === 'VSFeedback'.toLowerCase()) {
        return 'VSFeedback';
    }

    if (value.toLowerCase() === 'VisualStudioSetup'.toLowerCase()) {
        return 'VisualStudioSetup';
    }

    if (value.toLowerCase() === 'NuGet'.toLowerCase()) {
        return 'NuGet';
    }

    if (value.toLowerCase() === 'VisualStudioCopilot'.toLowerCase()) {
        return 'VisualStudioCopilot';
    }

    return undefined;
}

/**
 * Checks whether or not the given string value is a valid service identifier.
 * @param value Value to check
 * @param ignoreCase Whether or not check should ignore case. Default is false.
 * @returns Whether or not passed value is a valid service identifier
 */
export function isValidService (value: string, ignoreCase = false) {
    const compareValue = ignoreCase ? value.toLowerCase() : value;

    return compareValue === (ignoreCase ? 'GitHubCodespaces'.toLowerCase() : 'GitHubCodespaces')
        || compareValue === (ignoreCase ? 'IdentitySignIn'.toLowerCase() : 'IdentitySignIn')
        || compareValue === (ignoreCase ? 'Intellicode'.toLowerCase() : 'Intellicode')
        || compareValue === (ignoreCase ? 'Licensing'.toLowerCase() : 'Licensing')
        || compareValue === (ignoreCase ? 'LiveShare'.toLowerCase() : 'LiveShare')
        || compareValue === (ignoreCase ? 'Marketplace'.toLowerCase() : 'Marketplace')
        || compareValue === (ignoreCase ? 'NuGet'.toLowerCase() : 'NuGet')
        || compareValue === (ignoreCase ? 'Roaming'.toLowerCase() : 'Roaming')
        || compareValue === (ignoreCase ? 'VSFeedback'.toLowerCase() : 'VSFeedback')
        || compareValue === (ignoreCase ? 'VisualStudioSetup'.toLowerCase() : 'VisualStudioSetup')
        || compareValue === (ignoreCase ? 'VisualStudioCopilot'.toLowerCase() : 'VisualStudioCopilot');
}
